import { useState } from "react"
import { Plus, Trash2 } from "lucide-react"

import { ApiError } from "@/api/client"
import {
  useCreateAccountRequests,
  useRequestOptions,
} from "@/api/hooks/requests"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

import { RequestSideDrawer } from "./RequestSideDrawer"

const ACCOUNT_REQUEST_BATCH_MAX = 3
const ACCOUNT_REQUEST_TOP_UP_MAX = 500

type AccountRequestRow = {
  key: string
  networkSlug: string
  agency: string
  topUpAmount: string
}

function newRow(): AccountRequestRow {
  return {
    key: crypto.randomUUID(),
    networkSlug: "",
    agency: "",
    topUpAmount: "",
  }
}

type AccountRequestDrawerProps = {
  open: boolean
  onClose: () => void
  onSubmitted?: () => void
}

export function AccountRequestDrawer({
  open,
  onClose,
  onSubmitted,
}: AccountRequestDrawerProps) {
  const optionsQuery = useRequestOptions()
  const createMutation = useCreateAccountRequests()
  const [rows, setRows] = useState<AccountRequestRow[]>([newRow()])
  const [error, setError] = useState<string | null>(null)

  const networks = optionsQuery.data?.networks ?? []
  const agencies = optionsQuery.data?.agencies ?? []

  const resetAndClose = () => {
    setRows([newRow()])
    setError(null)
    onClose()
  }

  const onSubmit = async () => {
    setError(null)
    const items: Array<{
      network_slug: string
      agency: string
      top_up_amount: number
    }> = []
    for (const row of rows) {
      if (!row.networkSlug) {
        setError("Select an ad network for every row")
        return
      }
      if (!row.agency) {
        setError("Select an agency for every row")
        return
      }
      const topUp = Number(row.topUpAmount)
      if (
        !Number.isFinite(topUp) ||
        !Number.isInteger(topUp) ||
        topUp <= 0 ||
        topUp > ACCOUNT_REQUEST_TOP_UP_MAX
      ) {
        setError(
          `Enter a whole-dollar TopUp from 1 to ${ACCOUNT_REQUEST_TOP_UP_MAX} for every row`,
        )
        return
      }
      items.push({
        network_slug: row.networkSlug,
        agency: row.agency,
        top_up_amount: topUp,
      })
    }

    try {
      await createMutation.mutateAsync(items)
      onSubmitted?.()
      resetAndClose()
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Failed to submit account request"
      setError(message)
    }
  }

  return (
    <RequestSideDrawer
      open={open}
      title="Request new ad account"
      onClose={resetAndClose}
      footer={
        <div className="flex gap-3">
          <Button
            type="button"
            onClick={onSubmit}
            disabled={
              createMutation.isPending ||
              networks.length === 0 ||
              agencies.length === 0
            }
            className="bg-[#3d8b5c] text-white hover:bg-[#337350]"
          >
            {createMutation.isPending ? "Submitting..." : "Submit"}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={resetAndClose}
            className="bg-[#3a3a3a] text-white hover:bg-[#4a4a4a]"
          >
            Cancel
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error ? (
          <Alert
            variant="destructive"
            className="border-[#663333] bg-[#4a2a2a] text-[#f87171]"
          >
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        {optionsQuery.isLoading ? (
          <p className="text-sm text-[#b0b0b0]">Loading options...</p>
        ) : null}

        <div>
          <h3 className="mb-3 text-sm font-semibold text-[#e0e0e0]">
            Ad accounts<span className="text-[#f87171]">*</span>
          </h3>
          <div className="space-y-3">
            {rows.map((row, index) => (
              <div
                key={row.key}
                className="relative space-y-3 rounded-lg border border-[#4a4a4a] bg-[#2d2d2d] p-4"
              >
                {rows.length > 1 ? (
                  <button
                    type="button"
                    aria-label="Remove row"
                    className="absolute right-3 top-3 text-[#f87171] hover:text-[#fca5a5]"
                    onClick={() =>
                      setRows((prev) => prev.filter((r) => r.key !== row.key))
                    }
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                ) : null}

                <div className="space-y-1.5 pr-8">
                  <Label className="text-[#b0b0b0]">
                    Ad Network<span className="text-[#f87171]">*</span>
                  </Label>
                  <Select
                    value={row.networkSlug || undefined}
                    onValueChange={(value) =>
                      setRows((prev) =>
                        prev.map((r) =>
                          r.key === row.key
                            ? { ...r, networkSlug: value }
                            : r,
                        ),
                      )
                    }
                  >
                    <SelectTrigger className="border-[#555] bg-[#3a3a3a] text-white">
                      <SelectValue placeholder="Select network" />
                    </SelectTrigger>
                    <SelectContent className="border-[#555] bg-[#2d2d2d] text-white">
                      {networks.map((net) => (
                        <SelectItem key={net.slug} value={net.slug}>
                          {net.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-[#b0b0b0]">
                    Agency<span className="text-[#f87171]">*</span>
                  </Label>
                  <Select
                    value={row.agency || undefined}
                    onValueChange={(value) =>
                      setRows((prev) =>
                        prev.map((r) =>
                          r.key === row.key ? { ...r, agency: value } : r,
                        ),
                      )
                    }
                  >
                    <SelectTrigger className="border-[#555] bg-[#3a3a3a] text-white">
                      <SelectValue placeholder="Select agency" />
                    </SelectTrigger>
                    <SelectContent className="border-[#555] bg-[#2d2d2d] text-white">
                      {agencies.map((agency) => (
                        <SelectItem key={agency} value={agency}>
                          {agency}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-[#b0b0b0]">
                    TopUp<span className="text-[#f87171]">*</span>
                  </Label>
                  <div className="relative">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#b0b0b0]">
                      $
                    </span>
                    <Input
                      type="number"
                      min="1"
                      max={ACCOUNT_REQUEST_TOP_UP_MAX}
                      step="1"
                      inputMode="numeric"
                      value={row.topUpAmount}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/[^\d]/g, "")
                        setRows((prev) =>
                          prev.map((r) =>
                            r.key === row.key
                              ? { ...r, topUpAmount: digits }
                              : r,
                          ),
                        )
                      }}
                      className="border-[#555] bg-[#3a3a3a] pl-8 text-white"
                      placeholder={`1–${ACCOUNT_REQUEST_TOP_UP_MAX}`}
                      aria-label="TopUp amount"
                    />
                  </div>
                  <p className="text-xs text-[#888]">
                    Whole dollars, max ${ACCOUNT_REQUEST_TOP_UP_MAX}
                  </p>
                </div>

                <p className="text-xs text-[#888]">Request {index + 1}</p>
              </div>
            ))}
          </div>

          <div className="mt-4 flex justify-center">
            <Button
              type="button"
              variant="outline"
              onClick={() => setRows((prev) => [...prev, newRow()])}
              disabled={rows.length >= ACCOUNT_REQUEST_BATCH_MAX}
              className="border-[#555] bg-transparent text-white hover:bg-[#3a3a3a]"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add another account request
            </Button>
          </div>
        </div>
      </div>
    </RequestSideDrawer>
  )
}
